import type { PseoContent } from './content';

// Copy rules: before → after → bridge. Stats are product facts, never invented
// performance numbers. Only claim features that exist in the dashboard.
export const useCaseContent: Record<string, PseoContent> = {
  'customer-service': {
    headline: 'Customer Service With a Face, Not a Queue.',
    subheadline:
      'Swap the grey chat bubble for a 3D AI mascot that answers out loud from your help content, checks orders, and hands off to a human with the full context when it should.',
    features: [
      {
        title: 'Answers from your help center',
        body: 'Crawl your help center, upload files or connect Notion and Zendesk. It answers from your content, not from the open internet.',
      },
      {
        title: 'Warm handoff to humans',
        body: 'Frustrated or blocked visitors get a ticket in Intercom, Zendesk, HubSpot, Salesforce, Jira, Zoho or Help Scout, or a live Salesforce handoff, with the transcript attached.',
      },
      {
        title: 'Learns where it falls short',
        body: 'The Suggestions tab lists unanswered questions, missing content and contradictions, so it keeps getting better.',
      },
    ],
    painPoints: [
      { problem: 'Customers wait in a queue for simple answers', solution: 'The mascot answers instantly, every hour of the day.' },
      { problem: 'Agents repeat the same answers all day', solution: 'It takes the repetitive questions, and your team keeps the complex ones.' },
      { problem: 'Text bots feel cold and get ignored', solution: 'A friendly character with a voice gets noticed, and listened to.' },
    ],
    stats: [
      { value: '24/7', label: 'Service level' },
      { value: '7', label: 'Helpdesks for escalation' },
      { value: 'Live', label: 'Salesforce human handoff' },
    ],
    faqs: [
      {
        question: 'Can an AI mascot replace my support team?',
        answer: 'It handles the repetitive questions, like FAQs, order status and policies, and escalates the rest with full context. Your team spends its time where people are actually needed.',
      },
      {
        question: 'What happens when it does not know the answer?',
        answer: 'It says so and escalates: it opens a ticket in your helpdesk or hands off live to a Salesforce agent. The question also shows up in your dashboard so you can add the missing content.',
      },
      {
        question: 'Can I see what customers ask?',
        answer: 'Yes. Activity logs every conversation, and Analytics shows topics, sentiment, ratings, tickets and leads.',
      },
    ],
    ctaHeadline: 'Give your support a face customers trust',
    ctaBody: 'Create your support mascot free. Live on your site in minutes.',
  },

  'sales-assistant': {
    headline: 'A Sales Rep on Every Page. Awake at 3am.',
    subheadline:
      'A 3D AI mascot answers product and pricing questions out loud, spots buying intent, captures the lead and books the demo, before the visitor opens a competitor tab.',
    features: [
      {
        title: 'Intent-based lead capture',
        body: 'When a visitor shows buying intent, it asks for name, email and phone. No form, no friction.',
      },
      {
        title: 'Trained on your pitch',
        body: 'Product pages, pricing, case studies. It handles discovery questions the way your best rep would.',
      },
      {
        title: 'Demo booked in the chat',
        body: 'With Calendly or Cal.com connected, it offers real slots and confirms the meeting.',
      },
    ],
    painPoints: [
      { problem: 'Visitors leave without saying a word', solution: 'A greeting out loud starts the conversation a silent page never would.' },
      { problem: 'Leads go cold waiting for a reply', solution: 'Your mascot answers instantly, and Slack pings your team when a lead comes in.' },
      { problem: 'Reps chase prospects who were never a fit', solution: 'It qualifies first, so reps get the summary and the hot leads.' },
    ],
    stats: [
      { value: '24/7', label: 'Lead capture' },
      { value: 'Slack', label: 'Instant lead alerts' },
      { value: '2', label: 'Booking tools: Calendly & Cal.com' },
    ],
    faqs: [
      {
        question: 'How does the mascot qualify leads?',
        answer: 'It detects buying intent in the conversation, asks your qualifying questions, and captures contact details. You see every lead in the dashboard and can get notified in Slack.',
      },
      {
        question: 'Can it book demos into my calendar?',
        answer: 'Yes. Connect Calendly or Cal.com and it checks availability, books the slot and can cancel it if needed, all inside the conversation.',
      },
      {
        question: 'Can I send leads to my CRM?',
        answer: 'Use custom actions to call any webhook (POST, GET or PATCH) when a lead is captured or an intent is detected, and route the data wherever you need it.',
      },
    ],
    ctaHeadline: 'Never let a hot visitor leave in silence',
    ctaBody: 'Create your sales mascot free. Start capturing leads tonight.',
  },

  'hr-onboarding': {
    headline: 'New Hires Have 100 Questions. Give Them Someone to Ask.',
    subheadline:
      'A 3D AI mascot trained on your handbook answers benefits, leave and tooling questions, any time and without judgment, so HR is not the bottleneck.',
    features: [
      {
        title: 'Handbook Q&A',
        body: 'Upload your handbook and policies, or connect Notion. It answers from your documents only.',
      },
      {
        title: 'The questions people hesitate to ask',
        body: 'New hires ask a friendly character what they would not dare ask their manager twice.',
      },
      {
        title: 'Finds the gaps in your docs',
        body: 'It flags unanswered questions and contradictions between policies, so you can fix them.',
      },
    ],
    painPoints: [
      { problem: 'HR answers the same onboarding questions every week', solution: 'The mascot handles them, and HR keeps the conversations that matter.' },
      { problem: 'New hires feel lost in their first weeks', solution: 'Help is one question away, at any hour.' },
      { problem: 'Policies live in ten different places', solution: 'One friendly entry point to everything you have documented.' },
    ],
    stats: [
      { value: '24/7', label: 'Onboarding answers' },
      { value: 'Notion', label: 'Handbook sync' },
      { value: 'Scoped', label: 'To your documents' },
    ],
    faqs: [
      {
        question: 'Can it be trained on our employee handbook?',
        answer: 'Yes. Upload your handbook, benefits docs and checklists, or connect Notion. It answers only from that content.',
      },
      {
        question: 'Where do employees use it?',
        answer: 'On your intranet or any internal web page through the snippet, or inside your internal app with the React, React Native or Flutter SDKs.',
      },
      {
        question: 'How do we keep answers up to date?',
        answer: 'Update the source documents and re-sync. The Suggestions tab points out missing content and contradictions.',
      },
    ],
    ctaHeadline: 'Give every new hire a friendly first guide',
    ctaBody: 'Create your onboarding mascot free. No developers, no long setup.',
  },

  'lead-generation': {
    headline: 'Forms Feel Like Homework. Conversations Feel Like Help.',
    subheadline:
      'Replace the lead form with a 3D AI mascot that answers first and asks second. It captures name, email and phone when real intent shows up.',
    features: [
      {
        title: 'Captures on intent',
        body: 'It asks for contact details at the moment of interest, not in a pop-up the second someone lands.',
      },
      {
        title: 'Proactive and per-page',
        body: 'Set a proactive message after a delay and a different greeting per page URL, so pricing visitors hear a pricing opener.',
      },
      {
        title: 'Routes leads anywhere',
        body: 'Slack alerts for your team and webhooks to your CRM or automation tool.',
      },
    ],
    painPoints: [
      { problem: 'Visitors skip your forms', solution: 'They answer a friendly question in a conversation instead.' },
      { problem: 'Marketing leads arrive without context', solution: 'Every lead comes with the full conversation.' },
      { problem: 'Pop-ups annoy more than they convert', solution: 'A mascot that helps first earns the right to ask.' },
    ],
    stats: [
      { value: '24/7', label: 'Lead capture' },
      { value: 'Per-page', label: 'Greetings by URL' },
      { value: 'Webhooks', label: 'To any CRM' },
    ],
    faqs: [
      {
        question: 'Why use a conversation instead of a form?',
        answer: 'A form asks before it gives. A conversation answers the visitor first and asks for contact details once there is real interest, which is when people are willing to share.',
      },
      {
        question: 'What data does it capture?',
        answer: 'Name, email and phone on detected intent, plus the full conversation. You can also collect email at the start of the chat with the Collect email function.',
      },
      {
        question: 'How do leads reach my tools?',
        answer: 'Get Slack notifications, export conversations, or trigger a webhook to send each lead to your CRM or automation platform.',
      },
    ],
    ctaHeadline: 'Turn your website into a conversation',
    ctaBody: 'Create your lead gen mascot free. Start capturing leads tonight.',
  },

  'product-demo': {
    headline: 'Demo Your Product to Every Visitor, Without Booking 100 Calls.',
    subheadline:
      'A 3D AI mascot trained on your product walks each visitor through the features that matter to them, answers "does it integrate with X?" and books a live demo when they are ready.',
    features: [
      {
        title: 'Self-serve walkthroughs',
        body: 'It asks about role and goal, then explains the relevant features from your docs and product pages.',
      },
      {
        title: 'Objections, answered',
        body: 'Integrations, security, pricing. Train it on your sales FAQ and it answers mid-demo.',
      },
      {
        title: 'Live demo when it counts',
        body: 'When a prospect is ready for a human, it books the call with Calendly or Cal.com.',
      },
    ],
    painPoints: [
      { problem: 'Sales engineers are the bottleneck for every demo', solution: 'The mascot handles the self-serve questions, and SEs take the serious deals.' },
      { problem: 'Prospects lose interest waiting for a slot', solution: 'They get answers right now, on the page.' },
      { problem: 'Demo videos cannot answer questions', solution: 'Your mascot can, out loud.' },
    ],
    stats: [
      { value: '24/7', label: 'Demo availability' },
      { value: '9', label: 'Multilingual voices' },
      { value: '2', label: 'Booking tools: Calendly & Cal.com' },
    ],
    faqs: [
      {
        question: 'How does it personalise a product walkthrough?',
        answer: 'It asks the visitor about their role and goal, then answers from your content with the features that fit, so a developer and a marketer hear different things.',
      },
      {
        question: 'Can it handle objections?',
        answer: 'Yes. Add your sales FAQ and competitive answers to the knowledge base and it uses them in the conversation.',
      },
      {
        question: 'Can it hand over to a human for a live demo?',
        answer: 'Yes. It books a live demo through Calendly or Cal.com, or escalates to your team.',
      },
    ],
    ctaHeadline: 'Let your product explain itself, at any hour',
    ctaBody: 'Create your demo mascot free. Trained on your docs in minutes.',
  },

  'faq-automation': {
    headline: 'Stop Answering the Same Question Twice.',
    subheadline:
      'Your docs already hold the answers. A 3D AI mascot finds them and says them out loud, and it shows you exactly which questions your docs cannot answer yet.',
    features: [
      {
        title: 'Every source you have',
        body: 'Files, website crawl, plain text, Q&A pairs, Notion and Zendesk tickets.',
      },
      {
        title: 'Gap detection',
        body: 'Unanswered questions, missing content and contradictions are listed in Suggestions, ready to fix.',
      },
      {
        title: 'Honest when unsure',
        body: 'If the answer is not in your content, it says so and escalates to a human with the transcript.',
      },
    ],
    painPoints: [
      { problem: 'Nobody reads the help center', solution: 'People ask the mascot, and it reads the help center for them.' },
      { problem: 'Search bars return ten links and no answer', solution: 'One direct answer, from your own docs.' },
      { problem: 'You do not know what your docs are missing', solution: 'The mascot tells you, question by question.' },
    ],
    stats: [
      { value: '6', label: 'Knowledge source types' },
      { value: '3', label: 'Gap reports: unanswered, missing, contradictions' },
      { value: '24/7', label: 'Answers' },
    ],
    faqs: [
      {
        question: 'How does it stay accurate?',
        answer: 'It uses retrieval-augmented generation: for every question, it looks up the most relevant part of your content and answers from it, instead of improvising.',
      },
      {
        question: 'What can I upload?',
        answer: 'Files, website pages (crawl), plain text and Q&A pairs, plus Notion on Business and Zendesk tickets on Enterprise.',
      },
      {
        question: 'What if it cannot answer?',
        answer: 'It tells the visitor honestly, offers a human, and logs the question so you can add the answer.',
      },
    ],
    ctaHeadline: 'Turn your docs into a mascot that talks',
    ctaBody: 'Create your FAQ mascot free. Trained on your docs today.',
  },

  'appointment-booking': {
    headline: 'Fill Your Calendar Without the Back-and-Forth.',
    subheadline:
      'A 3D AI mascot answers the questions people ask before booking, checks your real availability and confirms the slot, all in one conversation.',
    features: [
      {
        title: 'Real availability',
        body: 'Connected to Calendly or Cal.com, it checks open slots, books and cancels inside the chat.',
      },
      {
        title: 'Answers before booking',
        body: 'Prices, duration, location, preparation. It removes the doubts that stop people from booking.',
      },
      {
        title: 'Booking button anywhere',
        body: 'Add a "Book a meeting" function to show your booking link as a button at the right moment.',
      },
    ],
    painPoints: [
      { problem: 'Staff lose hours on phone and email scheduling', solution: 'The mascot runs the whole booking conversation.' },
      { problem: 'People ask three questions before booking, then give up', solution: 'It answers them on the spot, then offers a slot.' },
      { problem: 'Late-night requests become voicemails', solution: 'It books 24/7.' },
    ],
    stats: [
      { value: '24/7', label: 'Booking' },
      { value: '2', label: 'Booking tools: Calendly & Cal.com' },
      { value: '0', label: 'Email back-and-forth' },
    ],
    faqs: [
      {
        question: 'Which calendars does it support?',
        answer: 'Calendly and Cal.com. The mascot can check availability, book a slot and cancel a booking.',
      },
      {
        question: 'Which businesses is this for?',
        answer: 'Any business that runs on appointments, like clinics, agencies, consultants, salons, real estate, coaching and services.',
      },
      {
        question: 'Can it qualify people before they book?',
        answer: 'Yes. It asks your questions first and shares the booking option when it makes sense.',
      },
    ],
    ctaHeadline: 'Let your mascot run your calendar',
    ctaBody: 'Create your booking mascot free. Taking appointments today.',
  },

  'training-coaching': {
    headline: 'Your Best Trainer, Available to Everyone at Once.',
    subheadline:
      'A 3D AI mascot trained on your training materials answers learner questions out loud, mid-module, at any hour, in a voice you choose or clone.',
    features: [
      {
        title: 'Your content, your voice',
        body: 'Upload training materials and pick a preset or cloned voice, so it explains things the way you would.',
      },
      {
        title: 'Questions, answered in the moment',
        body: 'Learners ask while they learn and get on-topic answers from your materials.',
      },
      {
        title: 'See what learners struggle with',
        body: 'Analytics shows the topics people ask about and the questions your content does not cover.',
      },
    ],
    painPoints: [
      { problem: 'Live sessions do not scale across time zones', solution: 'The mascot is available to every learner, any time.' },
      { problem: 'Learners get stuck between sessions', solution: 'They ask, and get an answer right away.' },
      { problem: 'Trainers repeat the same explanations', solution: 'The mascot covers the basics, and trainers coach.' },
    ],
    stats: [
      { value: '24/7', label: 'Learner support' },
      { value: 'Clone', label: 'Your own voice' },
      { value: '∞', label: 'Concurrent learners' },
    ],
    faqs: [
      {
        question: 'Does it replace my LMS?',
        answer: 'No. It sits on your training pages or inside your app and answers questions. Your LMS keeps tracking and certification.',
      },
      {
        question: 'Can it sound like me?',
        answer: 'Yes. Clone a voice from a short recording, or choose one of the preset multilingual voices.',
      },
      {
        question: 'How do I know what learners struggle with?',
        answer: 'Analytics surfaces topics and sentiment, and Suggestions lists the questions your materials did not answer.',
      },
    ],
    ctaHeadline: 'Train everyone, everywhere, at once',
    ctaBody: 'Create your training mascot free. Starting today.',
  },

  'brand-mascot': {
    headline: 'Your Mascot Has Been Silent for Years. Let It Talk.',
    subheadline:
      'Meta gave its agent Jolly. OpenAI gave its agents Dots. Give your brand character a 3D body, a voice and a brain, then put it to work on your site, your app and your stream.',
    features: [
      {
        title: '3D studio',
        body: 'Shape the look, outfit and personality of your character so it fits your brand.',
      },
      {
        title: 'A voice of its own',
        body: 'Pick from multilingual preset voices or clone a voice from a short recording.',
      },
      {
        title: 'It does more than wave',
        body: 'It answers from your content, captures leads, suggests next steps and books, with real-time lip-sync.',
      },
    ],
    painPoints: [
      { problem: 'Websites look the same and nobody remembers them', solution: 'A talking character is the thing visitors remember and share.' },
      { problem: 'Chat widgets feel clinical', solution: 'Your mascot brings warmth, humor and personality.' },
      { problem: 'Your mascot only lives on packaging and ads', solution: 'Now it greets every visitor in person.' },
    ],
    stats: [
      { value: '3D', label: 'Studio: look, outfit, personality' },
      { value: 'Clone', label: 'A voice from a recording' },
      { value: '3', label: 'Channels: web, apps, streams' },
    ],
    faqs: [
      {
        question: 'How does a 3D brand mascot work on a website?',
        answer: 'It runs as a widget on your site. It greets visitors, answers out loud with lip-sync, and guides them to products, offers or a booking.',
      },
      {
        question: 'Can it look like our existing mascot?',
        answer: 'Design it in the 3D studio to match your character as closely as possible, and give it a cloned or preset voice. Talk to us if you need a fully custom character.',
      },
      {
        question: 'Will it slow down my site?',
        answer: 'The widget loads in its own iframe and only expands when opened, so it stays out of the way of your page.',
      },
    ],
    ctaHeadline: 'Give your mascot a voice',
    ctaBody: 'Create it free in the 3D studio. Live in minutes.',
  },

  'in-chat-booking': {
    headline: 'Book, Buy and Pay, Without Leaving the Conversation.',
    subheadline:
      'A 3D AI mascot answers the last question, then acts. It books on Calendly or Cal.com, shows a checkout or offer button, and answers Stripe billing questions on the spot.',
    features: [
      {
        title: 'Booking in the chat',
        body: 'Visitors pick a slot without opening a new tab.',
      },
      {
        title: 'Buttons at the right moment',
        body: 'Custom buttons appear when the conversation reaches a specific moment, linking to checkout, a page or an event.',
      },
      {
        title: 'Stripe billing answers',
        body: 'Customers check invoices, subscriptions and payment status inside the chat.',
      },
    ],
    painPoints: [
      { problem: 'Every redirect loses people', solution: 'The next step happens in the conversation.' },
      { problem: 'Buyers hesitate at the last step', solution: 'The mascot answers the doubt, then shows the button.' },
      { problem: 'Unqualified people fill your calendar', solution: 'It qualifies first, then offers the slot.' },
    ],
    stats: [
      { value: '2', label: 'Booking tools: Calendly & Cal.com' },
      { value: 'Stripe', label: 'Billing in chat' },
      { value: '24/7', label: 'Conversions' },
    ],
    faqs: [
      {
        question: 'How does in-chat booking work?',
        answer: 'Connect Calendly or Cal.com. When a visitor wants to meet, the mascot checks availability and books the slot inside the widget.',
      },
      {
        question: 'Can it take payments?',
        answer: 'It can show a custom button that links to your checkout or payment link at the right moment. With Stripe connected, it also answers invoice and subscription questions.',
      },
      {
        question: 'Why does staying in the chat matter?',
        answer: 'Every new tab and form is a chance to drop off. Keeping the next step inside the conversation removes that friction.',
      },
    ],
    ctaHeadline: 'Turn the conversation into the conversion',
    ctaBody: 'Create your mascot free and turn on booking in minutes.',
  },
};
